import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHeader from "./SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

export default function Gazette() {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/dispatches/public`)
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]));
  }, []);

  return (
    <section id="gazette" className="py-20 lg:py-32 bg-[#0D1013]" data-testid="gazette-section">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <SectionHeader
          kicker="The Official Gazette"
          title="Directives & Union Dispatches"
          copy="Proclamations, ratifications, and milestones of the United States of Africa — published live from the Leadership Desk."
        />
        <div className="mt-14 divide-y divide-[#00A651]/12 border-y border-[#00A651]/12" data-testid="gazette-feed">
          {entries === null &&
            [0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse py-8" data-testid={`gazette-skeleton-${i}`}>
                <div className="h-3 w-20 bg-[#121519] rounded" />
                <div className="h-5 w-2/3 bg-[#121519] rounded mt-3" />
                <div className="h-3 w-1/2 bg-[#121519] rounded mt-3" />
              </div>
            ))}
          {entries !== null && entries.length === 0 && (
            <p className="py-10 font-official text-xs tracking-[0.2em] uppercase text-[#8A99AD]" data-testid="gazette-empty">
              The Gazette awaits its first dispatch — publish one from the Leadership Desk.
            </p>
          )}
          {entries !== null &&
            entries.map((e, i) => (
              <motion.article
                key={e.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline py-8 cursor-default"
                data-testid={`gazette-entry-${i + 1}`}
              >
                <p className="md:col-span-2 font-official text-[11px] tracking-[0.25em] text-[#00A651] uppercase">
                  {formatDate(e.published_at)}
                </p>
                <div className="md:col-span-9">
                  <p className="font-official text-[10px] tracking-[0.25em] uppercase text-[#8A99AD]">
                    {e.tag}
                    {e.scope === "council" && e.region ? ` • ${e.region} Council` : ""}
                    {e.scope === "country" && e.country_code ? ` • ${e.country_code}` : ""}
                  </p>
                  <h3 className="font-display font-bold text-lg sm:text-xl text-[#F7F5F0] mt-2 group-hover:text-[#00A651] transition-colors duration-300">
                    {e.title}
                  </h3>
                  <p className="text-sm text-[#8A99AD] mt-2 leading-relaxed max-w-2xl">{e.excerpt}</p>
                </div>
                <ArrowUpRight className="hidden md:block md:col-span-1 w-5 h-5 justify-self-end text-[#8A99AD] group-hover:text-[#00A651] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
              </motion.article>
            ))}
        </div>
      </div>
    </section>
  );
}
